/**
 * Sidebar menu visibility — mirrors backend getMenuItem (views.py) and
 * frontend RoleRoute / page-level gates in App.jsx.
 */

// Menu value keys returned by get_menu_item for each role
const ROLE_MENU_KEYS = {
  simple_employee: new Set([
    'profile_page', 'edit_my_info', 'daily_report', 'invoice_list',
    'ticket_list', 'my_missions', 'task_list',
    'current_map', 'setting',
    'company_list_view', 'user_list_view', 'add_company',
  ]),
  manager_employee: new Set([
    'profile_page', 'edit_my_info', 'daily_report', 'manager_daily_report',
    'daily_report_items', 'invoice_list', 'employee_feedback_page', 'manager_ticket_view',
    'referred_out_tickets', 'manager_mission_view', 'task_list',
    'employee_responsed_ticket', 'add_department_employee', 'current_map', 'setting',
    'company_list_view', 'user_list_view', 'add_company',
  ]),
  dispatcher: new Set([
    'profile_page', 'edit_my_info', 'dispatcher_ticket_view',
    'assigned_unanswered_tickets', 'employee_responsed_ticket', 'company_list_view',
    'department_list_view', 'user_list_view', 'employee_list_view', 'activation_history',
    'add_place', 'add_department', 'add_company', 'distance_report',
    'presence_report', 'daily_report_routing', 'invoice_list', 'setting',
    'ai_route_tickets', 'ai_services_monitor', 'sms_campaigns', 'employee_feedback_page',
  ]),
  manager_user: new Set([
    'profile_page', 'edit_my_info', 'ticket_form', 'smart_help', 'manager_user_unseen_ticket',
    'user_list_view', 'add_company_user', 'setting',
  ]),
  simple_user: new Set([
    'profile_page', 'edit_my_info', 'ticket_form', 'smart_help', 'simple_user_unseen_ticket', 'setting',
  ]),
  guest_user: new Set([
    'profile_page', 'edit_my_info', 'ticket_form', 'smart_help', 'simple_user_unseen_ticket', 'setting',
  ]),
};

// Paths with explicit RoleRoute or page-level gates (null value = all roles)
export const PATH_ALLOWED_ROLES = {
  '/tickets/new': ['simple_user', 'manager_user', 'guest_user'],
  '/help': ['simple_user', 'manager_user', 'guest_user'],
  '/tickets/referred': ['manager_employee'],
  '/dispatcher': ['dispatcher'],
  '/dispatcher/assigned': ['dispatcher'],
  '/dispatcher/ai-routing': ['dispatcher'],
  '/settings/error-alerts': ['admin'],
  '/sms-campaigns': ['dispatcher', 'admin'],
  '/settings/ai-services': ['dispatcher', 'admin'],
  '/dispatcher/daily-report-routing': ['dispatcher'],
  '/dispatcher/departments': ['dispatcher', 'manager_employee'],
  '/employees': ['dispatcher', 'manager_employee', 'admin'],
  '/companies': ['dispatcher', 'manager_employee', 'simple_employee'],
  '/companies/add': ['dispatcher', 'manager_employee', 'simple_employee'],
  '/departments': ['dispatcher', 'manager_employee', 'admin'],
  '/departments/add': ['dispatcher', 'admin'],
  '/users': ['dispatcher', 'manager_employee', 'simple_employee'],
  '/users/add/employee': ['manager_employee', 'dispatcher'],
  '/users/add/company': ['manager_user'],
  '/reports/invoices': ['simple_employee', 'manager_employee', 'dispatcher'],
  '/reports/manager': ['manager_employee'],
  '/reports/distance': ['dispatcher'],
  '/reports/presence': ['dispatcher'],
  '/activation-history': ['dispatcher', 'manager_employee'],
  '/settings/daily-report-items': ['manager_employee'],
  '/settings/auto-referral': ['manager_employee'],
  '/settings/cross-referral': ['dispatcher'],
  '/settings/username-prefix': ['dispatcher', 'admin'],
  '/tasks': ['simple_employee', 'manager_employee'],
  '/tasks/new': ['manager_employee'],
  '/settings/task-members': ['manager_employee'],
  '/missions/define': ['manager_employee'],
  '/missions/my': ['simple_employee'],
  '/feedback/team': ['manager_employee', 'dispatcher'],
  '/feedback/mine': ['manager_employee', 'dispatcher'],
  '/feedback': ['manager_employee', 'dispatcher'],
  '/map/places': ['dispatcher', 'manager_employee'],
  '/ai': ['manager_employee'],
  '/ai/manager': ['manager_employee'],
  '/ai/review': ['manager_employee'],
  '/ai/dataset': ['manager_employee'],
  '/ai/statistics': ['manager_employee'],
  '/ai/insights': ['manager_employee'],
};

const KEY_ALIASES = {
  edit_info: 'edit_my_info',
  send_ticket: 'ticket_form',
  add_employee: 'add_department_employee',
  add_user: 'add_company_user',
  mission_list: 'manager_mission_view',
  employee_dashboard: 'profile_page',
  my_dashboard: 'profile_page',
  messages: 'unseen_message',
  message_list: 'unseen_response_message',
  responsed_ticket: 'employee_responsed_ticket',
  ticket_form: 'ticket_form',
  menu_order: 'setting',
};

function normalizeKey(key) {
  if (!key || typeof key !== 'string') return '';
  const k = key.toLowerCase().replace(/\s+/g, '_');
  return KEY_ALIASES[k] || k;
}

export function isPathAllowedForRole(path, role) {
  if (!path || !role) return true;
  // admin is a super-role everywhere else (backend checkLogin, App.jsx
  // RoleRoute) - mirror that here so sidebar items aren't hidden for it.
  if (role === 'admin') return true;
  const allowed = PATH_ALLOWED_ROLES[path];
  if (!allowed) return true;
  return allowed.includes(role);
}

const HIDDEN_MENU_KEYS = new Set([
  'home',
  'unseen_message',
  'unseen_response_message',
  'messages',
  'message_list',
  'manager_ticket_view',
]);

const HIDDEN_MENU_LABELS = new Set([
  'Home', 'Messages', 'Message List', 'خانه', 'پیام‌ها', 'لیست پیام‌ها',
  'Ticket List', 'All Tickets', 'همه',
]);

function isHiddenMenuItem(item) {
  const key = normalizeKey(item?.key);
  const label = item?.label || item?.name || '';
  return HIDDEN_MENU_KEYS.has(key) || HIDDEN_MENU_LABELS.has(label);
}

export function isMenuKeyAllowed(key, role, pathForKey) {
  if (!key || key === 'Logout') return true;
  const normalized = normalizeKey(key);
  if (HIDDEN_MENU_KEYS.has(normalized)) return false;
  if (!role) return true;

  if (pathForKey && !isPathAllowedForRole(pathForKey, role)) return false;

  const allowedKeys = ROLE_MENU_KEYS[role];
  if (!allowedKeys) return true;
  return allowedKeys.has(normalized);
}

export function filterMenuByRole(items, role, toPath) {
  if (!Array.isArray(items)) return items || [];

  return items
    .map((item) => {
      if (isHiddenMenuItem(item)) return null;
      const children = item.children || item.sub_items;
      if (children?.length) {
        const filteredChildren = filterMenuByRole(children, role, toPath);
        if (filteredChildren.length === 0) return null;
        return { ...item, children: filteredChildren };
      }
      if (!role) return item;
      if (!item.key) return item;
      const path = toPath ? toPath(item.key) : null;
      return isMenuKeyAllowed(item.key, role, path) ? item : null;
    })
    .filter(Boolean);
}

const FALLBACK_MENU_KEY_BY_ID = {
  home: 'home',
  dashboard: 'profile_page',
  'new-ticket': 'ticket_form',
  'smart-help': 'smart_help',
  'ticket-list': 'manager_ticket_view',
  dispatcher: 'dispatcher_ticket_view',
  'daily-report': 'daily_report',
  'edit-profile': 'edit_my_info',
  settings: 'setting',
};

/** Load sidebar menu for role; falls back to allowlisted static items if API fails. */
export async function fetchRoleMenuItems(role, toPath, normalizeMenu) {
  const buildFallback = () => getFallbackMenuItems(role).map(({ id, label }) => ({
    label,
    key: FALLBACK_MENU_KEY_BY_ID[id] || id,
  }));

  try {
    const { BASE_URL } = await import('../api/client');
    const r = await fetch(`${BASE_URL}get_menu_item`, { credentials: 'include', cache: 'no-store' });
    if (!r.ok) throw new Error(`get_menu_item ${r.status}`);
    const data = await r.json();
    if (!data || typeof data !== 'object' || Array.isArray(data)) throw new Error('invalid menu payload');
    const raw = normalizeMenu(data);
    const filtered = filterMenuByRole(raw, role, toPath);
    if (filtered.length === 0) throw new Error('empty menu after role filter');
    return { items: filtered, unread: typeof data.count === 'number' ? data.count : 0, fromFallback: false };
  } catch {
    return { items: buildFallback(), unread: 0, fromFallback: true };
  }
}

export function getFallbackMenuItems(role) {
  const items = [
    { id: 'dashboard', to: '/dashboard', icon: 'dashboard', label: 'داشبورد', roles: ['simple_user', 'manager_user', 'guest_user', 'simple_employee', 'manager_employee'] },
    { id: 'new-ticket', to: '/tickets/new', icon: 'ticket', label: 'تیکت جدید', roles: ['simple_user', 'manager_user', 'guest_user'] },
    { id: 'smart-help', to: '/help', icon: 'ai', label: 'راهنمای هوشمند', roles: ['simple_user', 'manager_user', 'guest_user'] },
        { id: 'dispatcher', to: '/dispatcher', icon: 'list', label: 'صف دیسپچر', roles: ['dispatcher'] },
    { id: 'ai-routing', to: '/dispatcher/ai-routing', icon: 'ai', label: 'ارجاع با هوش مصنوعی', roles: ['dispatcher'] },
    { id: 'daily-report', to: '/reports/daily', icon: 'report', label: 'گزارش روزانه', roles: ['simple_employee', 'manager_employee'] },
    { id: 'edit-profile', to: '/dashboard', icon: 'edit', label: 'پروفایل', roles: null },
    { id: 'settings', to: '/settings', icon: 'settings', label: 'تنظیمات', roles: null },
  ];

  return items.filter((item) => {
    if (!item.roles) return true;
    return item.roles.includes(role);
  });
}

/** Brief labels per role for reporting — matches filtered backend menu. */
export const ROLE_MENU_SUMMARY = {
  simple_employee: 'داشبورد، گزارش روزانه، فاکتور، تیکت‌ها، ماموریت‌ها، نقشه، تنظیمات',
  manager_employee: 'داشبورد، گزارش روزانه/دپارتمان، بخش‌های گزارش، فاکتور، بازخورد تیم، تیکت‌ها، ارجاع‌شده، ماموریت‌ها، پاسخ‌داده، افزودن کارمند، نقشه، AI، تنظیمات',
  dispatcher: 'داشبورد، صف دیسپچر، ارجاع‌شده، لیست‌ها (شرکت/دپارتمان/کاربر/کارمند)، افزودن، گزارش‌ها، تنظیمات',
  manager_user: 'تیکت‌ها، ارسال تیکت، راهنمای هوشمند، لیست کاربران، افزودن کاربر، تنظیمات',
  simple_user: 'تیکت‌ها، ارسال تیکت، راهنمای هوشمند، تنظیمات',
  guest_user: 'تیکت‌ها، ارسال تیکت، تنظیمات',
};
